import { z } from 'zod'
import { GEOMETRY_MAX, GeometryCoord, Rect } from './geometry.js'
import { Field } from './manifest.js'

/**
 * "Place by text" anchor rules — Part Three D8, register §6/§8.
 *
 * A rule declares the anchor text, its document/page scope, case sensitivity,
 * whole-word matching, whether it targets one occurrence or all of them, and the
 * relative placement with signed horizontal/vertical offsets. The resolution output
 * carries the resolved coordinates and the manifest entry, which activation freezes.
 *
 * Anchor matching and normalization behaviour is S1.8 work; M0 freezes the shapes, and
 * the anchor normalization profile is an open freeze (register §10).
 */

export const ANCHOR_PROFILE_VERSION = 1 as const

const IsoDateTime = z.string().datetime()

/** Signed offset in the same millionths space as geometry. */
export const GeometryOffset = z
  .number()
  .int()
  .refine(
    (value) => value >= -GEOMETRY_MAX && value <= GEOMETRY_MAX,
    `offset must be within [${-GEOMETRY_MAX}, ${GEOMETRY_MAX}]`,
  )

export const AnchorDocumentScope = z.enum(['DOCUMENT', 'PAGE'])
export type AnchorDocumentScope = z.infer<typeof AnchorDocumentScope>

export const AnchorOccurrence = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('SPECIFIC'),
      /** One-based occurrence index within the scope. */
      occurrence_index: z.number().int().positive(),
    })
    .strict(),
  z.object({ kind: z.literal('ALL') }).strict(),
])
export type AnchorOccurrence = z.infer<typeof AnchorOccurrence>

export const AnchorRelativePlacement = z
  .object({
    horizontal: z.enum(['LEFT_OF_ANCHOR', 'RIGHT_OF_ANCHOR', 'CENTER_ON_ANCHOR']),
    vertical: z.enum(['ABOVE_ANCHOR', 'BELOW_ANCHOR', 'ALIGN_TOP', 'ALIGN_BOTTOM']),
    horizontal_offset: GeometryOffset.optional(),
    vertical_offset: GeometryOffset.optional(),
  })
  .strict()
export type AnchorRelativePlacement = z.infer<typeof AnchorRelativePlacement>

export const AnchorRule = z
  .object({
    anchor_id: z.string().min(1).max(64),
    field_id: z.string().min(1).max(64),
    profile_version: z.literal(ANCHOR_PROFILE_VERSION),
    anchor_text: z.string().min(1),
    document_scope: AnchorDocumentScope,
    page_index: GeometryCoord.optional(),
    case_sensitive: z.boolean(),
    whole_word: z.boolean(),
    occurrence: AnchorOccurrence,
    relative_placement: AnchorRelativePlacement,
  })
  .strict()
  .superRefine((rule, ctx) => {
    if (rule.document_scope === 'PAGE' && rule.page_index === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a page-scoped anchor names its page',
        path: ['page_index'],
      })
    }
    if (rule.document_scope === 'DOCUMENT' && rule.page_index !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'page_index is only valid for a page-scoped anchor',
        path: ['page_index'],
      })
    }
  })
export type AnchorRule = z.infer<typeof AnchorRule>

/**
 * Resolution output frozen at activation: the resolved rectangle plus the manifest
 * entry the field was materialized as. Acceptance is explicit — a resolution is never
 * applied silently.
 */
export const AnchorResolution = z
  .object({
    resolution_version: z.number().int().positive(),
    anchor_id: z.string().min(1).max(64),
    field_id: z.string().min(1).max(64),
    profile_version: z.literal(ANCHOR_PROFILE_VERSION),
    resolved_rect: Rect,
    manifest_entry: Field,
    explicitly_accepted: z.literal(true),
    resolved_at: IsoDateTime,
  })
  .strict()
  .superRefine((resolution, ctx) => {
    if (resolution.manifest_entry.id !== resolution.field_id) {
      ctx.addIssue({
        code: 'custom',
        message: 'the manifest entry belongs to the anchored field',
        path: ['manifest_entry', 'id'],
      })
    }
  })
export type AnchorResolution = z.infer<typeof AnchorResolution>
